import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Pencil, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SampleTracker() {
  const navigate = useNavigate();
  const [samples, setSamples] = useState([]);
  const [articles, setArticles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editSample, setEditSample] = useState(null);
  const [formData, setFormData] = useState({
    sample_number: '',
    article_id: '',
    article_name: '',
    customer_id: '',
    customer_name: '',
    status: 'in_progress',
    notes: ''
  });

  useEffect(() => {
    fetchSamples();
    fetchMasterData();
  }, []);

  const fetchMasterData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [articlesRes, customersRes] = await Promise.all([
        axios.get(`${API}/master-data?entity_type=article`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/master-data?entity_type=customer`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setArticles(articlesRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      console.error('Failed to fetch master data');
    }
  };

  const fetchSamples = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/samples`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSamples(response.data);
    } catch (error) {
      toast.error('Failed to fetch samples');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (editSample) {
        const payload = { ...formData, id: editSample.id };
        await axios.put(`${API}/samples/${editSample.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Updated successfully');
      } else {
        await axios.post(`${API}/samples`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Created successfully');
      }
      setOpen(false);
      setEditSample(null);
      setFormData({ sample_number: '', article_id: '', article_name: '', customer_id: '', customer_name: '', status: 'in_progress', notes: '' });
      fetchSamples();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/samples/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deleted successfully');
      fetchSamples();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-white shadow-md border-b mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Sample Article Tracker</h1>
              <p className="text-sm text-gray-600">Track sample progress</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex justify-end mb-6">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditSample(null); setFormData({ sample_number: '', article_id: '', article_name: '', customer_id: '', customer_name: '', status: 'in_progress', notes: '' }); }} data-testid="add-sample-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Sample
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="sample-dialog">
              <DialogHeader>
                <DialogTitle>{editSample ? 'Edit Sample' : 'Create Sample'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Sample Number</Label>
                  <Input value={formData.sample_number} onChange={(e) => setFormData({ ...formData, sample_number: e.target.value })} required data-testid="sample-number-input" />
                </div>
                <div className="space-y-2">
                  <Label>Article</Label>
                  <select className="w-full px-3 py-2 border rounded-md" value={formData.article_id}
                    onChange={(e) => {
                      const article = articles.find(a => a.id === e.target.value);
                      setFormData({ ...formData, article_id: e.target.value, article_name: article?.name || '' });
                    }} required data-testid="article-select">
                    <option value="">Select Article</option>
                    {articles.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Customer (Optional)</Label>
                  <select className="w-full px-3 py-2 border rounded-md" value={formData.customer_id}
                    onChange={(e) => {
                      const customer = customers.find(c => c.id === e.target.value);
                      setFormData({ ...formData, customer_id: e.target.value, customer_name: customer?.name || '' });
                    }} data-testid="customer-select">
                    <option value="">Select Customer</option>
                    {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <select className="w-full px-3 py-2 border rounded-md" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} data-testid="status-select">
                    <option value="in_progress">In Progress</option>
                    <option value="submitted">Submitted</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <textarea className="w-full px-3 py-2 border rounded-md" rows="3" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} data-testid="notes-input" />
                </div>
                <Button type="submit" className="w-full" data-testid="submit-sample">{editSample ? 'Update' : 'Create'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sample #</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Article</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {samples.map((sample) => (
                <tr key={sample.id} data-testid={`sample-row-${sample.id}`}>
                  <td className="px-6 py-4">{sample.sample_number}</td>
                  <td className="px-6 py-4">{sample.article_name}</td>
                  <td className="px-6 py-4">{sample.customer_name || 'N/A'}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      sample.status === 'approved' ? 'bg-green-100 text-green-800' :
                      sample.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      sample.status === 'submitted' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>{sample.status}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <Button variant="ghost" size="icon" onClick={() => { setEditSample(sample); setFormData(sample); setOpen(true); }} data-testid={`edit-sample-${sample.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(sample.id)} data-testid={`delete-sample-${sample.id}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {samples.length === 0 && <div className="text-center py-12" data-testid="no-samples"><p className="text-gray-500">No samples found</p></div>}
      </main>
    </div>
  );
}
